import { Feature } from 'ol';
import { Circle } from 'ol/geom';

import { isSelectableBox, selectBox, stampAnnotationNode } from './annotation.interaction';
import { pushHistory } from './boxHistory';
import { isInsideModal } from './modalDialog';
import { getActiveSelectFirst } from './selectFirst';

/**
 * Manages copy-paste functionality for annotations
 */
class CopyPasteManager {
	constructor() {
		this.copiedFeature = null;
		this.listeners = [];
	}

	/**
	 * Copy a feature's geometry and properties
	 * @param {Feature} feature - The feature to copy
	 */
	copy(feature) {
		if (!feature) {
			console.warn('No feature to copy');
			return false;
		}

		const geometry = feature.getGeometry();
		const properties = feature.getProperties();

		// Store the feature data
		this.copiedFeature = {
			geometryType: geometry.getType(),
			geometry: geometry.clone(),
			properties: {
				classId: properties.classId,
				annotation_type: properties.annotation_type,
				isInside: properties.isInside,
				// The object the box was drawn for, so a paste keeps its colour.
				nodeUuid: properties.nodeUuid,
				nodeColor: properties.nodeColor,
			},
		};

		// console.log('Feature copied');
		return true;
	}

	/**
	 * Paste the copied feature at a new location
	 * @param {Array} coordinates - The center coordinates where to paste [x, y]
	 * @param {VectorSource} source - The vector source to add the feature to
	 * @param {object} [options]
	 * @param {(feature: Feature) => boolean} [options.prepare] - runs on the new feature before
	 *   it is added; returning false drops the paste
	 * @returns {Feature|null} - The newly created feature or null
	 */
	paste(coordinates, source, { prepare } = {}) {
		if (!this.copiedFeature) {
			console.warn('No feature to paste');
			return null;
		}

		if (!coordinates || !source) {
			console.warn('Invalid paste parameters');
			return null;
		}

		const { geometry, properties } = this.copiedFeature;

		// Clone the geometry
		const newGeometry = geometry.clone();

		// Calculate the offset to move the geometry to the new location
		let currentCenter;
		if (newGeometry instanceof Circle) {
			currentCenter = newGeometry.getCenter();
		} else {
			// For polygons, calculate center from extent
			const extent = newGeometry.getExtent();
			currentCenter = [(extent[0] + extent[2]) / 2, (extent[1] + extent[3]) / 2];
		}

		const offsetX = coordinates[0] - currentCenter[0];
		const offsetY = coordinates[1] - currentCenter[1];

		// Translate the geometry to the new location
		newGeometry.translate(offsetX, offsetY);

		// Create a new feature with the translated geometry
		const newFeature = new Feature({
			geometry: newGeometry,
			...properties,
		});

		if (prepare && prepare(newFeature) === false) return null;

		// Add the feature to the source
		source.addFeature(newFeature);

		// console.log('Feature pasted');
		return newFeature;
	}

	/**
	 * Check if there's a feature available to paste
	 * @returns {boolean}
	 */
	hasCopiedFeature() {
		return this.copiedFeature !== null;
	}

	/**
	 * Clear the copied feature
	 */
	clear() {
		this.copiedFeature = null;
	}
}

// Create a singleton instance
const copyPasteManager = new CopyPasteManager();

export default copyPasteManager;

/**
 * Paste into a select-first session: the box belongs to the active object and the
 * open tile, a paste can be undone, and the pasted box is selected (Select mode, so
 * it can be moved at once). Dropped when it would land outside the tile.
 *
 * @returns {Feature|null} the pasted box
 */
const pasteScopedBox = ({ center, source, tileName }) => {
	const box = copyPasteManager.paste(center, source, {
		prepare: feature => {
			stampAnnotationNode(feature);
			if (tileName) feature.set('tileName', tileName, true);
			return isSelectableBox(feature);
		},
	});
	if (!box) return null;

	pushHistory({
		undo: () => {
			if (source.hasFeature(box)) source.removeFeature(box);
		},
	});
	getActiveSelectFirst()?.setMode('select');
	selectBox(box.get('boxId'));
	return box;
};

/**
 * Setup keyboard shortcuts for copy-paste functionality
 * @param {Map} map - OpenLayers map instance
 * @param {VectorSource} source - The vector source containing features
 * @param {Function} getSelectedFeature - Function that returns the currently selected feature
 * @param {Function} showToast - Optional function to show toast notifications
 * @param {object} [options]
 * @param {boolean} [options.scoped] - select-first session: the copy is the selected box, and a
 *   pasted box is stamped with the active object and the open tile (`getTileName`), can be
 *   undone, and ends up selected. The legacy paste is left as it was.
 * @param {() => string|null|undefined} [options.getTileName] - the open tile or image
 * @returns {Function} - Cleanup function to remove event listeners
 */
export const setupCopyPasteShortcuts = (
	map,
	source,
	getSelectedFeature,
	showToast = null,
	{ scoped = false, getTileName } = {}
) => {
	const handleKeyDown = event => {
		// Skip shortcuts when the target is editable
		const target = event.target;
		const isEditable =
			target &&
			(target.tagName === 'INPUT' ||
				target.tagName === 'TEXTAREA' ||
				target.isContentEditable);
		if (isEditable) return;
		// A modal (Train New Object, a confirm) covers the image: a paste would land on boxes
		// the user cannot see, and Ctrl+C belongs to the dialog's text.
		if (isInsideModal(target)) return;

		// Debug logging
		// console.log('[CopyPaste] Key pressed:', event.key, 'Ctrl:', event.ctrlKey, 'Meta:', event.metaKey);

		// Check for Ctrl+C or Cmd+C (copy)
		if ((event.ctrlKey || event.metaKey) && event.key === 'c') {
			// console.log('[CopyPaste] Copy shortcut detected');
			const selectedFeature = getSelectedFeature();
			// console.log('[CopyPaste] Selected feature:', selectedFeature);

			if (selectedFeature) {
				event.preventDefault();
				const success = copyPasteManager.copy(selectedFeature);
				// console.log('[CopyPaste] Copy result:', success);

				if (success && showToast) {
					showToast('Annotation copied! Press Ctrl+V to paste.', 'success');
				}
			} else {
				console.warn('[CopyPaste] No feature selected. Start modifying a feature first.');
				if (showToast) {
					showToast(
						scoped
							? 'No annotation selected. Select an annotation first.'
							: 'No annotation selected. Start modifying an annotation first.',
						'warning'
					);
				}
			}
		}

		// Check for Ctrl+V or Cmd+V (paste)
		if ((event.ctrlKey || event.metaKey) && event.key === 'v') {
			// console.log('[CopyPaste] Paste shortcut detected');
			// console.log('[CopyPaste] Has copied feature:', copyPasteManager.hasCopiedFeature());

			if (copyPasteManager.hasCopiedFeature()) {
				event.preventDefault();

				// Get the center of the current view
				const view = map.getView();
				const center = view.getCenter();
				// console.log('[CopyPaste] Pasting at center:', center);

				// Paste at the center of the view
				const newFeature = scoped
					? pasteScopedBox({ center, source, tileName: getTileName?.() })
					: copyPasteManager.paste(center, source);
				// console.log('[CopyPaste] Paste result:', newFeature);

				if (newFeature && showToast) {
					showToast('Annotation pasted! Drag to position it.', 'success');
				} else if (scoped && showToast) {
					showToast('Move the view over the image to paste.', 'warning');
				}
			} else {
				console.warn(
					'[CopyPaste] No feature in clipboard. Copy a feature first with Ctrl+C.'
				);
				if (showToast) {
					showToast('Nothing to paste. Copy an annotation first with Ctrl+C.', 'warning');
				}
			}
		}
	};

	// Add event listener
	document.addEventListener('keydown', handleKeyDown);
	// console.log('[CopyPaste] Keyboard shortcuts initialized');

	// Return cleanup function
	return () => {
		document.removeEventListener('keydown', handleKeyDown);
		// console.log('[CopyPaste] Keyboard shortcuts cleaned up');
	};
};
